import * as React from "react";
import { Check, ChevronDown, ChevronRight, FileText, Minus, X } from "lucide-react";
import { Badge, type PillTone } from "@hp/ui";
import type { WatchNote, WatchNoteChainLink, WatchNoteRoute, WatchNoteVerdict } from "@hp/api-client";
import { HoverHint } from "./HoverHint";
import { EmptyState } from "./Feedback";
import { formatDate } from "../lib/format";
import { DocumentButton, WatchNoteSourcesProvider } from "./watch-notes/SourceChips";
import { ChainTimeline, FactGroups, StatusTimeline } from "./watch-notes/WatchNoteSections";
import { hasFacts, noteDocument } from "./watch-notes/sourceHelpers";

/**
 * Watch notes: another party's ruling, a model clause or a supplier notice
 * that may matter to HP. They are deliberately not change events - nothing
 * HP was allowed or refused changed - so this file never touches the
 * opportunity or risk numbers, and the "hide events without an HP order"
 * setting does not apply here.
 */

export const VERDICT_CHIP: Record<WatchNoteVerdict, { label: string; tone: PillTone }> = {
  act_now: { label: "ACT NOW", tone: "danger" },
  watch: { label: "WATCH", tone: "warning" },
  no_action: { label: "NO ACTION", tone: "neutral" },
};

const KIND_LABEL: Record<WatchNote["kind"], string> = {
  norm_relaxation: "Norm relaxation",
  model_clause: "Model clause",
  supplier_notice: "Supplier notice",
  no_match: "No match",
};

const FACTOR_LABELS: [keyof NonNullable<WatchNote["rating"]>["factors"], string][] = [
  ["who", "Who"],
  ["what", "What"],
  ["where", "Where"],
  ["reliance", "Reliance"],
  ["our_files", "Our files"],
  ["status", "Status"],
];

/** "ACT NOW · 72/100", or the verdict alone when the note has no rating. */
export function verdictChipText(note: Pick<WatchNote, "verdict" | "rating">): string {
  const label = VERDICT_CHIP[note.verdict]?.label ?? note.verdict.toUpperCase();
  return note.rating ? `${label} · ${note.rating.score}/100` : label;
}

/**
 * The irrelevant orders (no_match) are only a log, shown behind a toggle;
 * every other note gets a card. Server order (newest document first) is kept
 * inside each group.
 */
export function splitWatchNotes(notes: WatchNote[]): { cards: WatchNote[]; noMatch: WatchNote[] } {
  return {
    cards: notes.filter((n) => n.kind !== "no_match"),
    noMatch: notes.filter((n) => n.kind === "no_match"),
  };
}

/**
 * The chain reads oldest first, like the history it is. Undated orders keep
 * the order the server gave and go last, since we cannot place them in time.
 */
export function chainOldestFirst(chain: WatchNoteChainLink[]): WatchNoteChainLink[] {
  const dated = chain.filter((c) => c.date).map((c, i) => ({ c, i }));
  dated.sort((a, b) => (a.c.date as string).localeCompare(b.c.date as string) || a.i - b.i);
  return [...dated.map((d) => d.c), ...chain.filter((c) => !c.date)];
}

/** "cercind.gov.in/2017/orders/277.pdf" from a full URL: the scheme adds nothing for a reader. */
export function displayFetchedFrom(url: string): string {
  return url.replace(/^https?:\/\//i, "").replace(/\/$/, "");
}

const ROUTE_RESULT_WORDS: Record<WatchNoteRoute["result"], string> = {
  succeeded: "yes",
  no: "no",
  failed: "failed",
  not_tested: "not tested",
};

function RouteMark({ route }: { route: WatchNoteRoute }) {
  const words = ROUTE_RESULT_WORDS[route.result] ?? route.result;
  const hint = `Route ${route.route}: ${route.name} - ${words}`;
  // A tick only for a route that reaches HP; "no" and "failed" both read as a cross,
  // failed in red because it was tried and fell short; untested is a dash.
  const mark =
    route.result === "succeeded" ? (
      <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
    ) : route.result === "failed" ? (
      <X className="h-3.5 w-3.5 text-red-500" aria-hidden />
    ) : route.result === "no" ? (
      <X className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
    ) : (
      <Minus className="h-3.5 w-3.5 text-zinc-300" aria-hidden />
    );
  return (
    <HoverHint hint={hint}>
      <span
        role="img"
        aria-label={hint}
        tabIndex={0}
        data-route-result={route.result}
        className={[
          "inline-flex h-6 min-w-[2.25rem] items-center justify-center gap-0.5 rounded border px-1 text-[0.6875rem] tabular-nums",
          route.result === "succeeded" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-zinc-200 bg-white text-zinc-500",
        ].join(" ")}
      >
        <span>{route.route}</span>
        {mark}
      </span>
    </HoverHint>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-2 text-sm">
      <dt className="text-xs font-medium uppercase tracking-wide text-zinc-400">{label}</dt>
      <dd className="text-zinc-800">{children}</dd>
    </div>
  );
}

/**
 * The note's own document under the title: its file name (opens it at page 1),
 * then forum, case reference, date and law-report citation - what a lawyer
 * would write to cite it. An older server sends only the file name.
 */
function NoteDocumentLine({ note }: { note: WatchNote }) {
  const doc = noteDocument(note);
  if (!doc) return null;
  const meta = [doc.forum, doc.case_ref, doc.date ? formatDate(doc.date) : null].filter(Boolean).join(" · ");
  return (
    <p className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-xs text-zinc-600">
      <FileText className="h-3 w-3 flex-none self-center text-zinc-400" aria-hidden />
      <DocumentButton doc={doc} className="font-medium">
        {doc.file_name}
      </DocumentButton>
      {meta ? <span>· {meta}</span> : null}
      {doc.citation ? <span className="italic text-zinc-700">· {doc.citation}</span> : null}
    </p>
  );
}

/** A full-width block under the facts: chain of orders and standing need the room a timeline takes. */
function CardSection({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="mt-3" aria-label={heading}>
      <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-400">{heading}</h5>
      {children}
    </section>
  );
}

/**
 * One watch note as a card: verdict and rating, the note's own document, the
 * facts with the page each was read from, the chain of orders, how the
 * precedent's standing changed, the routes and what to do. Every source opens
 * in the shared document viewer (see WatchNoteSourcesProvider).
 */
export function WatchNoteCard({ note }: { note: WatchNote }) {
  const chip = VERDICT_CHIP[note.verdict] ?? { label: note.verdict, tone: "neutral" as PillTone };
  const factorsHint = note.rating
    ? `Rating from six factors. ${FACTOR_LABELS.map(([key, label]) => `${label}: ${note.rating?.factors[key] ?? "-"}`).join("; ")}.`
    : "";
  const chipBadge = (
    <Badge tone={chip.tone} className="font-semibold tracking-wide" data-verdict={note.verdict}>
      {verdictChipText(note)}
    </Badge>
  );
  return (
    <WatchNoteSourcesProvider>
      <article className="rounded-md border border-zinc-200 bg-white px-4 py-3" aria-label={note.title}>
        <header className="flex flex-wrap items-start gap-x-2 gap-y-1">
          {note.rating ? <HoverHint hint={factorsHint}>{chipBadge}</HoverHint> : chipBadge}
          <h4 className="min-w-0 flex-1 text-sm font-semibold text-zinc-900">{note.title}</h4>
          <span className="text-[0.6875rem] uppercase tracking-wide text-zinc-400">{KIND_LABEL[note.kind] ?? note.kind}</span>
        </header>
        <NoteDocumentLine note={note} />
        <p className="mt-0.5 text-xs text-zinc-500">
          {note.subtitle}
          {note.fetched_from ? (
            <>
              {" · fetched: "}
              {/^https?:\/\//i.test(note.fetched_from) ? (
                <a href={note.fetched_from} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">
                  {displayFetchedFrom(note.fetched_from)}
                </a>
              ) : (
                note.fetched_from
              )}
            </>
          ) : null}
          {note.as_of ? ` · as of ${formatDate(note.as_of)}` : ""}
        </p>
        {/* Wide screens: the facts on the left, the orders, routes and what to do on the right. Narrower: one column. */}
        <div className="2xl:grid 2xl:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)] 2xl:gap-x-8">
        <div className="min-w-0">
        {hasFacts(note) ? (
          <FactGroups facts={note.facts ?? []} noteId={note.id} />
        ) : note.lines.length ? (
          // An older server sends the facts only as plain lines, without sources.
          <ul className="mt-2 space-y-1 text-sm text-zinc-800">
            {note.lines.map((line, i) => (
              <li key={i} className="leading-snug">
                {line}
              </li>
            ))}
          </ul>
        ) : null}
        </div>
        <div className="min-w-0 2xl:border-l 2xl:border-zinc-100 2xl:pl-6">
        {note.chain.length ? (
          <CardSection heading="Chain of orders">
            <ChainTimeline chain={chainOldestFirst(note.chain)} />
          </CardSection>
        ) : null}
        {note.status_history?.length ? (
          <CardSection heading="How its standing changed">
            <StatusTimeline history={note.status_history} />
          </CardSection>
        ) : null}
        <dl className="mt-3 space-y-1.5">
          {note.routes.length ? (
            <FieldRow label="Routes">
              <div className="flex flex-wrap gap-1" aria-label="The eight routes">
                {[...note.routes].sort((a, b) => a.route - b.route).map((route) => (
                  <RouteMark key={route.route} route={route} />
                ))}
              </div>
            </FieldRow>
          ) : null}
          {note.value ? <FieldRow label="Value">{note.value}</FieldRow> : null}
          {note.action ? <FieldRow label="Action">{note.action}</FieldRow> : null}
          {note.send_to ? <FieldRow label="Send to">{note.send_to}</FieldRow> : null}
        </dl>
        </div>
        </div>
      </article>
    </WatchNoteSourcesProvider>
  );
}

const VERDICT_ORDER: Record<string, number> = { act_now: 0, watch: 1, no_action: 2 };

/** The first fact of a kind, for a one-line column; the full list is in the opened card. */
function firstFact(note: WatchNote, kinds: string[]): string | null {
  return note.facts?.find((f) => kinds.includes(f.kind))?.text ?? null;
}

/** Click a link inside a row without also opening or closing the row. */
function StopRowClick({ children }: { children: React.ReactNode }) {
  return <span onClick={(e) => e.stopPropagation()}>{children}</span>;
}

function WatchNoteRow({ note }: { note: WatchNote }) {
  const [open, setOpen] = React.useState(false);
  const chip = VERDICT_CHIP[note.verdict] ?? { label: note.verdict, tone: "neutral" as PillTone };
  const doc = noteDocument(note);
  const holds = firstFact(note, ["holding", "finding", "rule"]) ?? note.lines[0] ?? null;
  const ours = firstFact(note, ["our_side"]);
  return (
    <>
      <tr
        className="cursor-pointer border-b border-zinc-100 align-top last:border-0 hover:bg-zinc-50"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <td className="min-w-[12rem] px-2 py-2">
          <div className="font-medium text-zinc-900">{note.title}</div>
          <div className="mt-0.5 text-xs font-medium text-sky-800">{KIND_LABEL[note.kind] ?? note.kind}</div>
          {note.subtitle ? <p className="mt-0.5 line-clamp-3 text-xs leading-snug text-zinc-600">{note.subtitle}</p> : null}
        </td>
        <td className="px-2 py-2">
          <Badge tone={chip.tone} className="whitespace-nowrap font-semibold tracking-wide" data-verdict={note.verdict}>
            {verdictChipText(note)}
          </Badge>
          {note.rating?.factors.where ? <div className="mt-1 max-w-[12rem] text-xs text-zinc-500">{note.rating.factors.where}</div> : null}
        </td>
        <td className="w-[13rem] max-w-[13rem] px-2 py-2 text-xs text-zinc-600">
          {doc ? (
            <>
              <div className="text-zinc-800">{[doc.forum, doc.date ? formatDate(doc.date) : null].filter(Boolean).join(" · ")}</div>
              {doc.case_ref ? <div>{doc.case_ref}</div> : null}
              {doc.citation ? <div className="italic text-zinc-700">{doc.citation}</div> : null}
              <StopRowClick>
                <DocumentButton doc={doc} className="mt-0.5 flex w-full min-w-0 items-center gap-1 overflow-hidden text-left">
                  <FileText className="h-3 w-3 flex-none" aria-hidden />
                  <span className="truncate">{doc.file_name}</span>
                </DocumentButton>
              </StopRowClick>
            </>
          ) : (
            note.file_name
          )}
        </td>
        <td className="px-2 py-2">
          <p className="line-clamp-4 min-w-[11rem] text-xs leading-snug text-zinc-700">{holds ?? "-"}</p>
        </td>
        <td className="px-2 py-2">
          <p className="line-clamp-4 min-w-[10rem] text-xs leading-snug text-zinc-700">{ours ?? "-"}</p>
        </td>
        <td className="px-2 py-2">
          <p className="line-clamp-4 min-w-[9rem] text-xs leading-snug text-zinc-800">{note.action ?? "-"}</p>
          {note.send_to ? <div className="mt-0.5 text-xs text-zinc-500">Send to: {note.send_to}</div> : null}
        </td>
        <td className="px-2 py-2 text-right">
          {open ? <ChevronDown className="ml-auto h-4 w-4 text-zinc-400" aria-hidden /> : <ChevronRight className="ml-auto h-4 w-4 text-zinc-400" aria-hidden />}
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-zinc-100 bg-zinc-50/60">
          <td colSpan={7} className="px-2 py-3">
            <WatchNoteCard note={note} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * The watch notes as a table, like the changed events above it: one row per note with its verdict and rating, the document it
 * comes from, what it holds, what it means on our side and the next step. A row opens the full card (every fact with its
 * source, the chain of orders, the routes) beneath it.
 */
export function WatchNotesTable({ notes }: { notes: WatchNote[] }) {
  const sorted = [...notes].sort(
    (a, b) => (VERDICT_ORDER[a.verdict] ?? 9) - (VERDICT_ORDER[b.verdict] ?? 9) || (b.rating?.score ?? 0) - (a.rating?.score ?? 0),
  );
  return (
    <div className="table-scroll">
      <table className="w-full min-w-[60rem] table-auto text-left text-sm">
        <thead>
          <tr className="border-b border-zinc-200 text-[0.6875rem] font-semibold uppercase tracking-wider text-zinc-500">
            <th className="px-2 py-2 font-semibold">Note</th>
            <th className="px-2 py-2 font-semibold">Verdict</th>
            <th className="px-2 py-2 font-semibold">Source</th>
            <th className="px-2 py-2 font-semibold">What it holds</th>
            <th className="px-2 py-2 font-semibold">Our side</th>
            <th className="px-2 py-2 font-semibold">Next step</th>
            <th className="w-6 px-2 py-2" aria-label="Open" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((note) => (
            <WatchNoteRow key={note.id} note={note} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The verdict counts shown beside the section title, e.g. "1 act now · 2 watch". */
export function watchNoteCounts(notes: WatchNote[]): Record<WatchNoteVerdict, number> {
  const counts: Record<WatchNoteVerdict, number> = { act_now: 0, watch: 0, no_action: 0 };
  for (const note of notes) if (note.verdict in counts) counts[note.verdict] += 1;
  return counts;
}

/**
 * The list under the section heading: one card per note, then the irrelevant
 * orders as grey log lines behind a "Checked, nothing found" toggle (closed
 * by default, so a reader is not asked to read what did not matter). Each
 * file name there opens its document, so "nothing found" can be checked.
 * One provider around the whole list means one document viewer for all cards.
 */
export function WatchNotesList({ notes, defaultShowNoMatch = false }: { notes: WatchNote[]; defaultShowNoMatch?: boolean }) {
  const [showNoMatch, setShowNoMatch] = React.useState(defaultShowNoMatch);
  const { cards, noMatch } = splitWatchNotes(notes);
  return (
    <WatchNoteSourcesProvider>
      <div className="space-y-3">
        {cards.length === 0 ? (
          <EmptyState>No watch notes yet: no other party's ruling or notice read so far looks like it matters to HP.</EmptyState>
        ) : (
          <WatchNotesTable notes={cards} />
        )}
        {noMatch.length > 0 ? (
          <div>
            <button
              type="button"
              aria-expanded={showNoMatch}
              onClick={() => setShowNoMatch((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800"
            >
              {showNoMatch ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
              Checked, nothing found ({noMatch.length})
            </button>
            {showNoMatch ? (
              <ul className="mt-1.5 space-y-0.5 pl-5 text-xs text-zinc-400" aria-label="Checked, nothing found">
                {noMatch.map((note) => {
                  const doc = noteDocument(note);
                  return (
                    <li key={note.id} className="truncate" title={[note.file_name, note.title, note.subtitle].filter(Boolean).join(" · ")}>
                      {note.as_of ? `${formatDate(note.as_of)} · ` : ""}
                      {doc ? <DocumentButton doc={doc} className="text-zinc-500">{note.file_name}</DocumentButton> : note.file_name}
                      {note.title ? ` · ${note.title}` : ""}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </WatchNoteSourcesProvider>
  );
}
